import React, { useState, useEffect } from "react";
import {
  ArrowRight,
  Calendar,
  ChevronLeft,
  FileText,
  Plus,
  Printer,
  Ticket,
  X,
  CheckCircle2,
  Trash,
  Info,
  Edit,
  Save,
  PackageCheck,
} from "lucide-react";
import { useParams, useNavigate, NavLink } from "react-router-dom";
import {
  TextField,
  Autocomplete,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  FormControlLabel,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { dummyOrders, dummyProducts } from "../../../../../dummyData";
import {
  errorToast,
  successToast,
} from "../../../../../services/toast.service";

// Sample tax types
const taxTypes = [
  { label: "SGST + CGST (Local)", value: "sgst_cgst" },
  { label: "IGST (Interstate)", value: "igst" },
  { label: "Export (Zero Rated)", value: "export" },
];

// Sample tax items
const initialTaxItems = [
  {
    id: 1,
    type: "Discount",
    operation: "-",
    percentage: "10.714",
    amount: "5528.42",
    notes: "Festival discount",
  },
  {
    id: 2,
    type: "SGST",
    operation: "+",
    percentage: "9.000",
    amount: "2775.42",
    notes: "State GST",
  },
  {
    id: 3,
    type: "CGST",
    operation: "+",
    percentage: "9.000",
    amount: "2775.42",
    notes: "Central GST",
  },
  {
    id: 4,
    type: "Frieght & Forwarding Charges",
    operation: "+",
    percentage: "5.000",
    amount: "1500.00",
    notes: "Shipping charges",
  },
];

const GenerateBillScreen = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [billDate, setBillDate] = useState(dayjs());
  const [voucherNo, setVoucherNo] = useState("RS/0001/22/23");
  const [gstType, setGstType] = useState(taxTypes[0]);
  const [taxItems, setTaxItems] = useState(initialTaxItems);
  const [isAddingTax, setIsAddingTax] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [newTax, setNewTax] = useState({
    type: "",
    operation: "+",
    percentage: "",
    amount: "",
    notes: "",
  });
  const [orderItems, setOrderItems] = useState([
    {
      id: "item1",
      productId: "prod002",
      productName: "Anarkali Style Salwar Suit",
      color: "Royal Blue",
      hsn: "5407",
      size: "M",
      unit: "Pcs",
      mrp: 3299,
      sellingPrice: 2699,
      quantity: 3,
      total: 8097,
    },
    {
      id: "item2",
      productId: "prod001",
      productName: "Chikankari Embroidered Salwar Suit",
      color: "Pink",
      hsn: "5407",
      size: "Free Size",
      unit: "Pcs",
      mrp: 2499,
      sellingPrice: 1899,
      quantity: 5,
      total: 9495,
    },
    {
      id: "item3",
      productId: "prod003",
      productName: "Designer Palazzo Suit",
      color: "Mint Green",
      hsn: "5407",
      size: "L",
      unit: "Pcs",
      mrp: 1999,
      sellingPrice: 1499,
      quantity: 4,
      total: 5996,
    },
  ]);
  const [editedItems, setEditedItems] = useState([]);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSellAsType, setSelectedSellAsType] = useState("primary");
  const [selectedSellAsOption, setSelectedSellAsOption] = useState("");
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [newItemData, setNewItemData] = useState({
    quantity: 1,
    size: "",
    color: "",
  });

  // Sample sell as options for each product
  const sampleSellAsOptions = {
    primary: [{ label: "Individual Unit", value: "primary" }],
    secondary: [
      { label: "Standard Pack (8 pieces)", value: "standard_pack" },
      { label: "Display Pack (2 pieces)", value: "display_pack" },
    ],
    tertiary: [{ label: "Carton Pack (40 pieces)", value: "carton_pack" }],
  };

  // Initialize edited items
  useEffect(() => {
    setEditedItems([...orderItems]);
  }, [orderItems]);

  // Find order details
  const order = dummyOrders.find(o => o._id === orderId) || dummyOrders[0];

  // Total calculation
  const subtotal = 23588;
  const discount = 5528.42;
  const tax = 5550.84;
  const grandTotal = 34099;

  const handleAddTaxItem = () => {
    // Validate inputs
    if (!newTax.type || !newTax.percentage || !newTax.amount) {
      errorToast("Please fill in all tax details");
      return;
    }

    // Add new tax item
    const newItem = {
      id: taxItems.length + 1,
      ...newTax,
    };

    setTaxItems([...taxItems, newItem]);
    successToast(`${newTax.type} added successfully`);

    // Reset form
    setNewTax({
      type: "",
      operation: "+",
      percentage: "",
      amount: "",
      notes: "",
    });

    setIsAddingTax(false);
  };

  const handleDeleteTaxItem = id => {
    const itemToDelete = taxItems.find(item => item.id === id);
    setTaxItems(taxItems.filter(item => item.id !== id));
    successToast(`${itemToDelete?.type || "Tax item"} removed successfully`);
  };

  const handleAcceptOrder = () => {
    successToast("Order accepted successfully");
    setSuccessModalOpen(true);
  };

  const handlePrintBill = () => {
    // Printing functionality would go here
    console.log("Printing bill...");
    successToast("Bill sent to printer");
  };

  // Handle item field edit
  const handleEditItem = (id, field, value) => {
    const updatedItems = editedItems.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };

        // Recalculate total if quantity or sellingPrice changes
        if (field === "quantity" || field === "sellingPrice") {
          updatedItem.total = updatedItem.quantity * updatedItem.sellingPrice;
        }

        return updatedItem;
      }
      return item;
    });

    setEditedItems(updatedItems);
  };

  // Handle item deletion
  const handleDeleteItem = id => {
    setEditedItems(editedItems.filter(item => item.id !== id));
    successToast("Item removed from order");
  };

  // Save changes
  const handleSaveChanges = () => {
    setOrderItems([...editedItems]);
    setEditMode(false);
    successToast("Changes saved successfully");
  };

  // Cancel changes
  const handleCancelEdit = () => {
    setEditedItems(orderItems.map(item => ({ ...item })));
    setEditMode(false);
    successToast("Edits canceled");
  };

  // Calculate totals based on items
  const calculateTotals = items => {
    const subtotal = items.reduce((sum, item) => sum + item.total, 0);
    return subtotal;
  };

  // Open Add Product Dialog
  const handleAddProductClick = () => {
    setProductDialogOpen(true);
    setSelectedProduct(null);
    setSelectedSellAsType("primary");
    setSelectedSellAsOption("");
    setNewItemData({
      quantity: 1,
      size: "",
      color: "",
    });
    setSelectedTabIndex(0);
  };

  // Close Product Dialog
  const handleCloseProductDialog = () => {
    setProductDialogOpen(false);
  };

  // Handle product selection
  const handleProductSelection = product => {
    setSelectedProduct(product);
    if (product) {
      setNewItemData({
        ...newItemData,
        size: product.size?.split(", ")[0] || "",
        color: product.color || "",
      });
    } else {
      // Handle clearing the selection
      setSelectedProduct(null);
      setNewItemData({
        quantity: 1,
        size: "",
        color: "",
      });
    }
  };

  // Handle tab change in product dialog
  const handleTabChange = (event, newValue) => {
    setSelectedTabIndex(newValue);
  };

  // Add new item to order
  const handleAddItem = () => {
    if (!selectedProduct) {
      errorToast("Please select a product first");
      return;
    }

    // Calculate quantity and price based on sell as type
    let finalQuantity = newItemData.quantity;
    let packSize = 1;

    if (selectedSellAsType === "secondary") {
      packSize = 8; // Standard secondary pack has 8 items
      if (selectedSellAsOption === "display_pack") {
        packSize = 2; // Display pack has 2 items
      }
    } else if (selectedSellAsType === "tertiary") {
      packSize = 40; // Carton pack has 40 items
    }

    // Create new item
    const newItem = {
      id: `item${Date.now()}`,
      productId: selectedProduct._id,
      productName: selectedProduct.name,
      hsn: "5407", // Default HSN code for textiles
      size:
        selectedSellAsType === "primary"
          ? newItemData.size
          : selectedSellAsType === "secondary"
          ? "M, L, XL" // For secondary packs, show all sizes
          : "Standard Pack", // For tertiary packs
      color: selectedProduct.color,
      unit: "Pcs",
      mrp: selectedProduct.mrp,
      sellingPrice: selectedProduct.sellingPrice,
      quantity: finalQuantity,
      packSize: packSize,
      totalItems: finalQuantity * packSize,
      total: selectedProduct.sellingPrice * finalQuantity * packSize,
      sellAsType: selectedSellAsType,
    };

    setEditedItems([...editedItems, newItem]);
    setProductDialogOpen(false);
    // If not in edit mode, save the changes immediately
    if (!editMode) {
      setOrderItems([...orderItems, newItem]);
    }
    successToast("Product added to order successfully");
  };

  return (
    <div className="bg-[#F9F8FF] min-h-screen">
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b bg-white shadow-sm">
        <div className="flex items-center gap-4">
          <NavLink
            to={`/orders/orderdetail/${orderId}`}
            className="p-1.5 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors">
            <ChevronLeft size={20} className="text-gray-600" />
          </NavLink>
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Generate Bill
          </h1>
        </div>
      </div>

      {/* Main content */}
      <div className="bg-white shadow-sm border border-gray-200">
        {/* Top section with date, voucher and GST type */}
        <div className="p-6 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h2 className="text-sm font-medium text-gray-600 mb-2">
                Bill Date
              </h2>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={billDate}
                  onChange={newValue => setBillDate(newValue)}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Calendar size={18} className="text-[#5652B0]" />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </div>

            <div>
              <h2 className="text-sm font-medium text-gray-600 mb-2">
                Voucher Number
              </h2>
              <TextField
                value={voucherNo}
                onChange={e => setVoucherNo(e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FileText size={18} className="text-[#5652B0]" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div>
              <h2 className="text-sm font-medium text-gray-600 mb-2">
                GST Type
              </h2>
              <FormControl fullWidth size="small">
                <Select
                  value={gstType.value}
                  onChange={e => {
                    const selected = taxTypes.find(
                      t => t.value === e.target.value,
                    );
                    setGstType(selected);
                  }}
                  displayEmpty>
                  {taxTypes.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-base font-medium text-gray-800 mb-4 flex items-center">
            <Info size={18} className="text-[#5652B0] mr-2" />
            Customer Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Firm Name:</span>
              <span className="font-medium text-gray-800">
                {order.firmName}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Phone Number:</span>
              <span className="font-medium text-gray-800">
                {order.phoneNumber}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Customer Name:</span>
              <span className="font-medium text-gray-800">Chaitanya Goyal</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">GSTIN:</span>
              <span className="font-medium text-gray-800">
                {order.gstin || "78115264569"}
              </span>
            </div>

            <div className="flex md:col-span-2">
              <span className="text-gray-600 mr-4">Shop Address:</span>
              <span className="font-medium text-gray-800">{order.address}</span>
            </div>
          </div>
        </div>

        {/* Order Items Table */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-base font-medium text-gray-800 flex items-center">
              <FileText size={18} className="text-[#5652B0] mr-2" />
              Order Items
            </h2>

            <div className="flex gap-2">
              {editMode ? (
                <>
                  <button
                    onClick={handleSaveChanges}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#5652B0] text-white rounded-md hover:bg-[#4A47A3] transition-colors">
                    <Save size={15} />
                    <span>Save Changes</span>
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors">
                    <X size={15} />
                    <span>Cancel</span>
                  </button>
                </>
              ) : order.status !== "Approved" ? (
                <>
                  <button
                    onClick={handleAddProductClick}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#F9F8FE] text-[#5652B0] border border-[#EFEDFB] rounded-md hover:bg-[#F3F0FF] transition-colors">
                    <Plus size={15} />
                    <span>Add Item</span>
                  </button>
                  <button
                    onClick={() => setEditMode(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[#5652B0] text-[#5652B0] rounded-md hover:bg-[#F9F8FF] transition-colors">
                    <Edit size={15} />
                    <span>Edit Items</span>
                  </button>
                </>
              ) : (
                <button
                  onClick={handlePrintBill}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[#5652B0] text-[#5652B0] rounded-md hover:bg-[#F9F8FF] transition-colors">
                  <Printer size={15} />
                  <span>Print Bill</span>
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <TableContainer
              component={Paper}
              elevation={0}
              className="border border-gray-200 rounded-md">
              <Table size="small">
                <TableHead className="bg-[#F9F8FF]">
                  <TableRow>
                    <TableCell className="font-medium text-gray-700">
                      Sr.
                    </TableCell>
                    <TableCell className="font-medium text-gray-700">
                      Product
                    </TableCell>
                    <TableCell className="font-medium text-gray-700">
                      HSN/SAC
                    </TableCell>
                    <TableCell className="font-medium text-gray-700">
                      Size
                    </TableCell>
                    <TableCell className="font-medium text-gray-700">
                      Unit
                    </TableCell>
                    <TableCell
                      className="font-medium text-gray-700"
                      align="right">
                      MRP
                    </TableCell>
                    <TableCell
                      className="font-medium text-gray-700"
                      align="right">
                      Price
                    </TableCell>
                    <TableCell
                      className="font-medium text-gray-700"
                      align="right">
                      Qty
                    </TableCell>
                    <TableCell
                      className="font-medium text-gray-700"
                      align="right">
                      Amount
                    </TableCell>
                    {editMode && (
                      <TableCell
                        className="font-medium text-gray-700"
                        width="60px"></TableCell>
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {(editMode ? editedItems : orderItems).map((item, index) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {item.productName} - {item.color}
                        </div>
                        <div className="text-xs text-gray-500">
                          #{item.productId}
                        </div>
                      </TableCell>
                      <TableCell>{item.hsn}</TableCell>
                      <TableCell>
                        {editMode ? (
                          <TextField
                            value={item.size}
                            onChange={e =>
                              handleEditItem(item.id, "size", e.target.value)
                            }
                            variant="outlined"
                            size="small"
                            sx={{
                              width: "80px",
                              "& .MuiInputBase-root": {
                                height: "32px",
                              },
                            }}
                          />
                        ) : (
                          item.size
                        )}
                      </TableCell>
                      <TableCell>{item.unit}</TableCell>
                      <TableCell align="right">
                        ₹{item.mrp.toLocaleString()}
                      </TableCell>
                      <TableCell align="right">
                        {editMode ? (
                          <TextField
                            value={item.sellingPrice}
                            onChange={e =>
                              handleEditItem(
                                item.id,
                                "sellingPrice",
                                parseInt(e.target.value) || 0,
                              )
                            }
                            variant="outlined"
                            size="small"
                            type="number"
                            sx={{
                              width: "100px",
                              "& .MuiInputBase-root": {
                                height: "32px",
                              },
                            }}
                          />
                        ) : (
                          `₹${item.sellingPrice.toLocaleString()}`
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {editMode ? (
                          <TextField
                            value={item.quantity}
                            onChange={e =>
                              handleEditItem(
                                item.id,
                                "quantity",
                                parseInt(e.target.value) || 0,
                              )
                            }
                            variant="outlined"
                            size="small"
                            type="number"
                            inputProps={{ min: 1 }}
                            sx={{
                              width: "80px",
                              "& .MuiInputBase-root": {
                                height: "32px",
                              },
                            }}
                          />
                        ) : (
                          item.quantity
                        )}
                      </TableCell>
                      <TableCell align="right" className="font-medium">
                        ₹{item.total.toLocaleString()}
                      </TableCell>
                      {editMode && (
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteItem(item.id)}
                            color="error">
                            <Trash size={15} />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}

                  {/* Grand Total Row */}
                  <TableRow className="border-t border-gray-200">
                    <TableCell
                      colSpan={editMode ? 8 : 7}
                      align="right"
                      className="font-medium text-gray-800 py-3">
                      Grand Total:
                    </TableCell>
                    <TableCell align="right" className="py-3">
                      {/* Total quantity */}
                      {(editMode ? editedItems : orderItems).reduce(
                        (sum, item) => sum + item.quantity,
                        0,
                      )}
                    </TableCell>
                    <TableCell
                      align="right"
                      className="font-extrabold text-gray-900 py-3 text-[16px]">
                      ₹
                      {calculateTotals(
                        editMode ? editedItems : orderItems,
                      ).toLocaleString()}
                    </TableCell>
                    {editMode && <TableCell></TableCell>}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>

        {/* Tax and Voucher Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6 border-b border-gray-200">
          {/* Redeemed Vouchers */}
          <div className="md:col-span-1">
            <h2 className="text-base font-medium text-gray-800 mb-3 flex items-center">
              <Ticket size={18} className="text-[#5652B0] mr-2" />
              Redeemed Vouchers
            </h2>

            <div className="space-y-2">
              <div className="bg-[#F9F8FF] border border-[#EFEDFB] rounded-md p-3 flex items-start gap-2">
                <Ticket size={16} className="text-[#5652B0] mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-800">
                    FIRST50
                  </div>
                  <div className="text-xs text-gray-500">
                    ₹500 off on your first order
                  </div>
                </div>
              </div>

              <div className="bg-[#F9F8FF] border border-[#EFEDFB] rounded-md p-3 flex items-start gap-2">
                <Ticket size={16} className="text-[#5652B0] mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-800">
                    SUMMER23
                  </div>
                  <div className="text-xs text-gray-500">
                    ₹200 off on orders above ₹2000
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tax Summary */}
          <div className="md:col-span-2">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-base font-medium text-gray-800 flex items-center">
                <FileText size={18} className="text-[#5652B0] mr-2" />
                Tax & Additional Charges
              </h2>

              <button
                onClick={() => {
                  setTaxItems([
                    ...taxItems,
                    {
                      id: taxItems.length + 1,
                      type: "",
                      operation: "+",
                      percentage: "",
                      amount: "",
                    },
                  ]);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#5652B0] border border-[#5652B0] rounded-md hover:bg-[#F9F8FF]">
                <Plus size={14} />
                <span>Add</span>
              </button>
            </div>

            <TableContainer
              component={Paper}
              elevation={0}
              className="border border-gray-200 rounded-md">
              <Table size="small">
                <TableHead className="bg-[#F9F8FF]">
                  <TableRow>
                    <TableCell className="font-medium text-gray-700" width="5%">
                      #
                    </TableCell>
                    <TableCell
                      className="font-medium text-gray-700"
                      width="40%">
                      Description
                    </TableCell>
                    <TableCell
                      className="font-medium text-gray-700"
                      width="10%">
                      +/-
                    </TableCell>
                    <TableCell
                      className="font-medium text-gray-700"
                      width="20%">
                      Rate (%)
                    </TableCell>
                    <TableCell
                      className="font-medium text-gray-700"
                      width="20%">
                      Amount
                    </TableCell>
                    <TableCell
                      className="font-medium text-gray-700"
                      width="5%"></TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {taxItems.map((item, index) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Autocomplete
                          value={item.type}
                          onChange={(event, newValue) => {
                            const updatedItems = [...taxItems];
                            updatedItems[index].type = newValue;
                            setTaxItems(updatedItems);
                          }}
                          options={[
                            "SGST",
                            "CGST",
                            "IGST",
                            "Discount",
                            "Freight & Forwarding",
                            "Round Off",
                            "Labour Charges",
                          ]}
                          renderInput={params => (
                            <TextField
                              {...params}
                              size="small"
                              placeholder="Type"
                              sx={{
                                "& .MuiInputBase-root": {
                                  height: "32px",
                                  fontSize: "0.875rem",
                                },
                              }}
                            />
                          )}
                          freeSolo
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <Select
                            value={item.operation}
                            onChange={e => {
                              const updatedItems = [...taxItems];
                              updatedItems[index].operation = e.target.value;
                              setTaxItems(updatedItems);
                            }}
                            sx={{
                              height: "32px",
                              fontSize: "0.875rem",
                            }}>
                            <MenuItem value="+">+</MenuItem>
                            <MenuItem value="-">-</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={item.percentage}
                          onChange={e => {
                            const updatedItems = [...taxItems];
                            updatedItems[index].percentage = e.target.value;
                            setTaxItems(updatedItems);
                          }}
                          size="small"
                          placeholder="0.00"
                          fullWidth
                          sx={{
                            "& .MuiInputBase-root": {
                              height: "32px",
                              fontSize: "0.875rem",
                            },
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">%</InputAdornment>
                            ),
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={item.amount}
                          onChange={e => {
                            const updatedItems = [...taxItems];
                            updatedItems[index].amount = e.target.value;
                            setTaxItems(updatedItems);
                          }}
                          size="small"
                          placeholder="0.00"
                          fullWidth
                          sx={{
                            "& .MuiInputBase-root": {
                              height: "32px",
                              fontSize: "0.875rem",
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                ₹
                              </InputAdornment>
                            ),
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteTaxItem(item.id)}
                          className="text-gray-400 hover:text-red-500">
                          <Trash size={14} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>

        {/* Bill Summary */}
        <div className="p-6">
          <div className="flex justify-end">
            <div className="w-full max-w-xs">
              <div className="space-y-2">
                <div className="flex justify-between py-1 text-gray-700">
                  <span>Subtotal:</span>
                  <span className="font-medium">
                    ₹{subtotal.toLocaleString()}
                  </span>
                </div>

                <div className="flex justify-between py-1 text-green-600">
                  <span>Discount:</span>
                  <span className="font-medium">
                    -₹{discount.toLocaleString()}
                  </span>
                </div>

                <div className="flex justify-between py-1 text-gray-700">
                  <span>Tax (18% GST):</span>
                  <span className="font-medium">₹{tax.toLocaleString()}</span>
                </div>

                <Divider className="my-2" />

                <div className="flex justify-between pt-1 text-[#5652B0]">
                  <span className="font-semibold">Grand Total:</span>
                  <span className="font-bold text-lg">
                    ₹{grandTotal.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handlePrintBill}
            className="flex items-center gap-2 px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm">
            <Printer size={18} />
            <span className="font-medium">Print Bill</span>
          </button>

          <button
            onClick={handleAcceptOrder}
            className="flex items-center gap-2 px-5 py-2.5 text-white bg-[#5652B0] hover:bg-[#4A47A3] rounded-lg transition-colors shadow-sm">
            <CheckCircle2 size={18} />
            <span className="font-medium">Accept Order</span>
          </button>
        </div>
      </div>

      {/* Success Modal */}
      <Dialog
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "0",
          },
        }}>
        <DialogContent className="p-0">
          <div className="relative">
            <div className="absolute top-4 right-4">
              <IconButton
                onClick={() => setSuccessModalOpen(false)}
                size="small"
                className="bg-white hover:bg-gray-100">
                <X size={18} />
              </IconButton>
            </div>

            <div className="bg-[#5652B0] text-white py-8 px-4 text-center rounded-t-xl">
              <div className="flex justify-center mb-4">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
                  <CheckCircle2 size={40} className="text-white" />
                </div>
              </div>

              <h2 className="text-2xl font-semibold mb-1">
                Order Successfully Accepted
              </h2>
              <p className="text-white/80">
                Order has been successfully accepted for {order.firmName}
              </p>
            </div>

            <div className="p-6">
              <div className="bg-gray-50 rounded-lg p-4 flex justify-around mb-6">
                <div className="text-center">
                  <div className="text-sm text-gray-500">Bill Amount</div>
                  <div className="text-xl font-semibold text-gray-800">
                    ₹{grandTotal.toLocaleString()}
                  </div>
                </div>

                <div className="text-center">
                  <div className="text-sm text-gray-500">Invoice Date</div>
                  <div className="text-xl font-semibold text-gray-800">
                    {dayjs().format("DD/MM/YYYY")}
                  </div>
                </div>
              </div>

              <div className="flex justify-center gap-4">
                <button
                  onClick={handlePrintBill}
                  className="flex items-center gap-2 px-5 py-3 text-[#5652B0] bg-white border border-[#5652B0] rounded-lg hover:bg-[#F9F8FF] transition-colors">
                  <Printer size={18} />
                  <span className="font-medium">Print Bill</span>
                </button>

                <button
                  onClick={() => navigate("/orders")}
                  className="flex items-center gap-2 px-5 py-3 text-white bg-[#5652B0] hover:bg-[#4A47A3] rounded-lg transition-colors">
                  <span className="font-medium">Go to Orders</span>
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Product Dialog */}
      <Dialog
        open={productDialogOpen}
        onClose={handleCloseProductDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            height: "80vh",
            maxHeight: "700px",
            display: "flex",
            flexDirection: "column",
          },
        }}>
        <DialogTitle
          sx={{
            p: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "#F9F8FE",
          }}>
          <span className="text-xl font-semibold text-gray-800">
            Add Product to Order
          </span>
          <IconButton onClick={handleCloseProductDialog} edge="end">
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, flex: 1, overflow: "auto" }}>
          <div className="flex flex-col md:flex-row h-full">
            {/* Left side: Search & Product Selection */}
            <div className="w-full md:w-1/3 border-r border-gray-200 bg-white p-4 overflow-auto">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Products
                </label>
                <Autocomplete
                  options={dummyProducts}
                  getOptionLabel={option => option?.name || ""}
                  filterOptions={(options, state) => {
                    const inputValue = state.inputValue.toLowerCase().trim();
                    if (!inputValue) return options;

                    return options.filter(
                      option =>
                        option.name.toLowerCase().includes(inputValue) ||
                        option.brand.name.toLowerCase().includes(inputValue) ||
                        option.productId.toLowerCase().includes(inputValue),
                    );
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder="Search by name, brand or code..."
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  )}
                  onChange={(event, newValue) => {
                    handleProductSelection(newValue);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      className="py-2 px-3 hover:bg-[#F9F8FE] border-b border-gray-100 last:border-0">
                      <div className="flex items-start gap-2">
                        <div className="w-8 h-8 bg-[#F9F8FE] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <PackageCheck size={15} className="text-[#5652B0]" />
                        </div>
                        <div className="flex flex-col">
                          <div className="font-medium text-gray-800">
                            {option.name}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
                            <span className="font-medium bg-gray-100 px-1.5 py-0.5 rounded">
                              {option.productId}
                            </span>
                            <span>{option.brand.name}</span>
                            <span className="text-[#5652B0] font-medium ml-auto">
                              ₹{option.sellingPrice}
                            </span>
                          </div>
                        </div>
                      </div>
                    </li>
                  )}
                  ListboxProps={{
                    style: {
                      maxHeight: "350px",
                    },
                  }}
                  PopperProps={{
                    style: {
                      width: "fit-content",
                      minWidth: "100%",
                      maxWidth: "450px",
                    },
                    placement: "bottom-start",
                  }}
                  noOptionsText={
                    <div className="py-2 px-3 text-sm text-gray-500 text-center">
                      No products found
                    </div>
                  }
                />
              </div>

              {selectedProduct && (
                <div className="mt-4 bg-[#F9FAFB] p-3 rounded-md border border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-[#F9F8FE] rounded-full flex items-center justify-center">
                      <PackageCheck size={16} className="text-[#5652B0]" />
                    </div>
                    <h3 className="font-medium text-gray-800">
                      {selectedProduct.name}
                    </h3>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">SKU:</span>
                      <span className="font-medium">
                        {selectedProduct.productId}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Brand:</span>
                      <span className="font-medium">
                        {selectedProduct.brand.name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">MRP:</span>
                      <span className="text-gray-500 line-through">
                        ₹{selectedProduct.mrp}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Selling Price:</span>
                      <span className="font-medium text-[#5652B0]">
                        ₹{selectedProduct.sellingPrice}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right side: Configuration options */}
            <div className="w-full md:w-2/3 p-4 overflow-auto">
              {!selectedProduct ? (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                  <PackageCheck size={48} className="mb-3 text-gray-300" />
                  <p>Please select a product to continue</p>
                </div>
              ) : (
                <>
                  <h3 className="text-lg font-medium text-gray-800 mb-4">
                    Add to Order
                  </h3>

                  {/* Sell As Options */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      How would you like to sell?
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div
                        className={`border rounded-lg p-3 cursor-pointer transition-colors
                          ${
                            selectedSellAsType === "primary"
                              ? "border-[#5652B0] bg-[#F9F8FE]"
                              : "border-gray-200 hover:bg-gray-50"
                          }`}
                        onClick={() => setSelectedSellAsType("primary")}>
                        <div className="flex items-center mb-1">
                          <Radio
                            checked={selectedSellAsType === "primary"}
                            sx={{
                              padding: 0,
                              marginRight: 1,
                              color: "#5652B0",
                              "&.Mui-checked": { color: "#5652B0" },
                            }}
                          />
                          <span className="font-medium">Individual Unit</span>
                        </div>
                        <p className="text-xs text-gray-500 ml-5">
                          Sell as single items
                        </p>
                      </div>

                      <div
                        className={`border rounded-lg p-3 cursor-pointer transition-colors
                          ${
                            selectedSellAsType === "secondary"
                              ? "border-[#5652B0] bg-[#F9F8FE]"
                              : "border-gray-200 hover:bg-gray-50"
                          }`}
                        onClick={() => setSelectedSellAsType("secondary")}>
                        <div className="flex items-center mb-1">
                          <Radio
                            checked={selectedSellAsType === "secondary"}
                            sx={{
                              padding: 0,
                              marginRight: 1,
                              color: "#5652B0",
                              "&.Mui-checked": { color: "#5652B0" },
                            }}
                          />
                          <span className="font-medium">Secondary Pack</span>
                        </div>
                        <p className="text-xs text-gray-500 ml-5">
                          Multiple units in a pack
                        </p>
                      </div>

                      <div
                        className={`border rounded-lg p-3 cursor-pointer transition-colors
                          ${
                            selectedSellAsType === "tertiary"
                              ? "border-[#5652B0] bg-[#F9F8FE]"
                              : "border-gray-200 hover:bg-gray-50"
                          }`}
                        onClick={() => setSelectedSellAsType("tertiary")}>
                        <div className="flex items-center mb-1">
                          <Radio
                            checked={selectedSellAsType === "tertiary"}
                            sx={{
                              padding: 0,
                              marginRight: 1,
                              color: "#5652B0",
                              "&.Mui-checked": { color: "#5652B0" },
                            }}
                          />
                          <span className="font-medium">Tertiary Pack</span>
                        </div>
                        <p className="text-xs text-gray-500 ml-5">
                          Bulk carton packaging
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Pack Type Selection (if applicable) */}
                  {selectedSellAsType !== "primary" && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Pack Type
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {sampleSellAsOptions[selectedSellAsType].map(option => (
                          <div
                            key={option.value}
                            className={`border rounded-lg p-3 cursor-pointer transition-colors
                              ${
                                selectedSellAsOption === option.value
                                  ? "border-[#5652B0] bg-[#F9F8FE]"
                                  : "border-gray-200 hover:bg-gray-50"
                              }`}
                            onClick={() =>
                              setSelectedSellAsOption(option.value)
                            }>
                            <div className="flex items-center mb-1">
                              <Radio
                                checked={selectedSellAsOption === option.value}
                                sx={{
                                  padding: 0,
                                  marginRight: 1,
                                  color: "#5652B0",
                                  "&.Mui-checked": { color: "#5652B0" },
                                }}
                              />
                              <span className="font-medium">
                                {option.label}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pack Configuration (if applicable) */}
                  {selectedSellAsType !== "primary" && selectedSellAsOption && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pack Configuration
                      </label>
                      <div className="bg-[#F9FAFB] border border-gray-200 rounded-lg overflow-hidden">
                        <TableContainer component={Paper} elevation={0}>
                          <Table size="small">
                            <TableHead className="bg-[#F9F8FE]">
                              <TableRow>
                                <TableCell className="font-medium text-gray-700 py-2">
                                  Size
                                </TableCell>
                                <TableCell className="font-medium text-gray-700 py-2">
                                  Color
                                </TableCell>
                                <TableCell
                                  align="center"
                                  className="font-medium text-gray-700 py-2">
                                  Quantity
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedSellAsType === "secondary" ? (
                                selectedSellAsOption === "display_pack" ? (
                                  <>
                                    <TableRow>
                                      <TableCell className="py-2">M</TableCell>
                                      <TableCell className="py-2">
                                        {selectedProduct.color}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        className="py-2">
                                        1
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell className="py-2">L</TableCell>
                                      <TableCell className="py-2">
                                        {selectedProduct.color}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        className="py-2">
                                        1
                                      </TableCell>
                                    </TableRow>
                                  </>
                                ) : (
                                  <>
                                    <TableRow>
                                      <TableCell className="py-2">M</TableCell>
                                      <TableCell className="py-2">
                                        {selectedProduct.color}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        className="py-2">
                                        3
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell className="py-2">L</TableCell>
                                      <TableCell className="py-2">
                                        {selectedProduct.color}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        className="py-2">
                                        3
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell className="py-2">XL</TableCell>
                                      <TableCell className="py-2">
                                        {selectedProduct.color}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        className="py-2">
                                        2
                                      </TableCell>
                                    </TableRow>
                                  </>
                                )
                              ) : (
                                <>
                                  <TableRow>
                                    <TableCell className="py-2">
                                      Standard Pack
                                    </TableCell>
                                    <TableCell className="py-2">
                                      {selectedProduct.color}
                                    </TableCell>
                                    <TableCell align="center" className="py-2">
                                      5
                                    </TableCell>
                                  </TableRow>
                                </>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </div>
                    </div>
                  )}

                  {/* Order Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {selectedSellAsType === "primary" && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Size
                        </label>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          value={newItemData.size}
                          onChange={e =>
                            setNewItemData({
                              ...newItemData,
                              size: e.target.value,
                            })
                          }>
                          {selectedProduct.size.split(", ").map(size => (
                            <MenuItem key={size} value={size}>
                              {size}
                            </MenuItem>
                          ))}
                        </TextField>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {selectedSellAsType === "primary"
                          ? "Quantity"
                          : `Number of ${
                              selectedSellAsType === "secondary"
                                ? "Packs"
                                : "Cartons"
                            }`}
                      </label>
                      <TextField
                        fullWidth
                        type="number"
                        size="small"
                        value={newItemData.quantity}
                        onChange={e =>
                          setNewItemData({
                            ...newItemData,
                            quantity: parseInt(e.target.value) || 1,
                          })
                        }
                        inputProps={{ min: 1 }}
                      />
                    </div>
                  </div>

                  {/* Price summary section */}
                  <div className="mt-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                      Price Summary
                    </h3>

                    <div className="space-y-2">
                      {selectedProduct && (
                        <>
                          {selectedSellAsType === "primary" ? (
                            <>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">
                                  Unit Price:
                                </span>
                                <span className="font-medium">
                                  ₹
                                  {selectedProduct.sellingPrice.toLocaleString()}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Quantity:</span>
                                <span className="font-medium">
                                  {newItemData.quantity}
                                </span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">
                                  Pack Price:
                                </span>
                                <span className="font-medium">
                                  ₹
                                  {selectedSellAsType === "secondary"
                                    ? (
                                        selectedProduct.sellingPrice *
                                        (selectedSellAsOption === "display_pack"
                                          ? 2
                                          : 8)
                                      ).toLocaleString()
                                    : (
                                        selectedProduct.sellingPrice * 40
                                      ).toLocaleString()}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">
                                  Number of Packs:
                                </span>
                                <span className="font-medium">
                                  {newItemData.quantity}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">
                                  Items per Pack:
                                </span>
                                <span className="font-medium">
                                  {selectedSellAsType === "secondary"
                                    ? selectedSellAsOption === "display_pack"
                                      ? 2
                                      : 8
                                    : 40}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">
                                  Total Items:
                                </span>
                                <span className="font-medium">
                                  {newItemData.quantity *
                                    (selectedSellAsType === "secondary"
                                      ? selectedSellAsOption === "display_pack"
                                        ? 2
                                        : 8
                                      : 40)}
                                </span>
                              </div>
                            </>
                          )}

                          <div className="flex justify-between text-sm pt-2 mt-1 border-t border-gray-200">
                            <span className="font-medium text-gray-700">
                              Subtotal:
                            </span>
                            <span className="font-semibold text-[#5652B0]">
                              ₹
                              {(
                                selectedProduct.sellingPrice *
                                newItemData.quantity *
                                (selectedSellAsType === "primary"
                                  ? 1
                                  : selectedSellAsType === "secondary"
                                  ? selectedSellAsOption === "display_pack"
                                    ? 2
                                    : 8
                                  : 40)
                              ).toLocaleString()}
                            </span>
                          </div>

                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">
                              Tax (18% GST):
                            </span>
                            <span className="font-medium">
                              ₹
                              {Math.round(
                                selectedProduct.sellingPrice *
                                  newItemData.quantity *
                                  (selectedSellAsType === "primary"
                                    ? 1
                                    : selectedSellAsType === "secondary"
                                    ? selectedSellAsOption === "display_pack"
                                      ? 2
                                      : 8
                                    : 40) *
                                  0.18,
                              ).toLocaleString()}
                            </span>
                          </div>

                          <div className="flex justify-between text-sm pt-2 mt-1 border-t border-gray-200">
                            <span className="font-medium text-gray-700">
                              Total:
                            </span>
                            <span className="font-semibold text-[#5652B0]">
                              ₹
                              {Math.round(
                                selectedProduct.sellingPrice *
                                  newItemData.quantity *
                                  (selectedSellAsType === "primary"
                                    ? 1
                                    : selectedSellAsType === "secondary"
                                    ? selectedSellAsOption === "display_pack"
                                      ? 2
                                      : 8
                                    : 40) *
                                  1.18,
                              ).toLocaleString()}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogContent>

        <DialogActions
          sx={{ p: 3, bgcolor: "#FAFAFA", borderTop: "1px solid #E5E7EB" }}>
          <Button
            onClick={handleCloseProductDialog}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderColor: "rgba(0, 0, 0, 0.23)",
              color: "rgba(0, 0, 0, 0.87)",
            }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddItem}
            variant="contained"
            disabled={
              !selectedProduct ||
              (selectedSellAsType !== "primary" && !selectedSellAsOption)
            }
            startIcon={<Plus size={16} />}
            sx={{
              textTransform: "none",
              bgcolor: "#5652B0",
              "&:hover": {
                bgcolor: "#4A47A3",
              },
            }}>
            Add to Order
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GenerateBillScreen;
